import { useNavigate } from 'react-router-dom';
import { MouseEvent, useState, useCallback } from 'react';
import { useTheme } from 'styled-components';
import { DriverDetails } from '../../../API/types/Drivers';
import useDriverThumbnail from '../../../theme/useDriverThumbnail';
import useVehicleThumbnail from '../../../theme/useVehicleThumbnail';
import Modal from '../../../components/common/Modal/Modal';
import Tags from '../../../components/common/Tags';
import { Button } from '../../../components/ui/Button/Button';
import { ListItemWrapper } from '../../../components/ui/ListItemWrapper/ListItemWrapper';
import EntityLink from '../../../components/ui/EntityLink/EntityLink';
import { ThemeType } from '../../../theme/themeType';
import { getFormattedDate } from '../../../utils/dateUtils';
import { generateDistance } from '../../../utils/unitsUtils';
import { IconKeys } from '../../../components/common/Icon/IconKeys';
import { AssignVehicleToDriver } from '../Modals';
import useGetRole from '../../../API/hooks/useGetRole';
import { generateScoreColor, getScoreLabel, hasDisplayableEntityScore } from '../../../utils/commonUtils';
import CheckBox from '../../../components/common/CheckBox/CheckBox';
import { ListNavigationState } from '../../../utils/listNavigation';

type Props = {
  item?: DriverDetails;
  isSelecting?: boolean;
  isSelected?: boolean;
  onSelect?: (checked: boolean, id: string) => void;
  lightVersion?: boolean;
  period?: string;
  listNavigation?: ListNavigationState;
};

function DriversItem({
  item, lightVersion, isSelected, isSelecting, onSelect, period = 'monthly', listNavigation,
}: Props): JSX.Element {
  const theme = useTheme() as ThemeType;
  const vehicleThumbnail = useVehicleThumbnail();
  const driverThumbnail = useDriverThumbnail();
  const navigate = useNavigate();
  const { isFleetViewer } = useGetRole();
  const [modalState, toggleModalState] = useState(false);

  const {
    _id: id,
    avatarUrl,
    firstName,
    lastName,
    vehicle,
    distanceInKmTotal,
    distanceInKmDaily,
    distanceInKmWeekly,
    distanceInKmMonthly,
    alertCountDaily,
    alertCountWeekly,
    alertCountMonthly,
    alertCountTotal,
    licenseName,
    licenseExpireAt,
    fleetId,
    score,
    scoreDaily,
    scoreWeekly,
    scoreMonthly,
  } = item!;

  let displayScore: number | null;
  if (period === 'daily') displayScore = scoreDaily ?? null;
  else if (period === 'weekly') displayScore = scoreWeekly ?? null;
  else if (period === 'monthly') displayScore = scoreMonthly ?? null;
  else displayScore = score ?? null;

  let distance = distanceInKmTotal;
  if (period === 'daily') distance = distanceInKmDaily;
  else if (period === 'weekly') distance = distanceInKmWeekly;
  else if (period === 'monthly') distance = distanceInKmMonthly;

  let distanceForScore = distanceInKmTotal;
  if (period === 'daily') distanceForScore = distanceInKmDaily;
  else if (period === 'weekly') distanceForScore = distanceInKmWeekly;
  else if (period === 'monthly') distanceForScore = distanceInKmMonthly;
  const showScore = hasDisplayableEntityScore(displayScore, distanceForScore);

  let alerts: number | undefined = alertCountTotal;
  if (period === 'daily') alerts = alertCountDaily;
  else if (period === 'weekly') alerts = alertCountWeekly;
  else if (period === 'monthly') alerts = alertCountMonthly;

  const vehicleDetailsId = isSelecting ? undefined : (vehicle?._id || vehicle?.vehicleId);

  const handleAssignVehicle = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    toggleModalState(true);
  };

  const handleClick = useCallback((): void => {
    if (isSelecting) {
      if (onSelect) {
        onSelect!(isSelected as boolean, id);
      }
      return;
    }

    navigate(`/drivers/${id}`, { state: { listNavigation } });
  }, [id, isSelecting, isSelected, listNavigation, navigate, onSelect]);

  return (
    <>
      <ListItemWrapper
        onClick={handleClick}
        data-list-item-id={id}
      >
        {isSelecting && (
          <CheckBox
            label=""
            state={isSelected}
            onChange={() => { }}
            name={`driver-${id}`}
          />
        )}
        {showScore && (
          <Tags.Status label={getScoreLabel(displayScore!)} backgroundColor={generateScoreColor(displayScore!)} />
        )}
        {!showScore && (
          <Tags.Status label="No score" backgroundColor={theme.colors.outline} title="This driver didn't have any trips yet." />
        )}
        <Tags.ItemOverview
          avatar={avatarUrl || driverThumbnail}
          avatarAlt="driver"
          title={`${firstName} ${lastName}`}
          subtitle={licenseName && licenseExpireAt ? `${licenseName} (exp. ${licenseExpireAt ? getFormattedDate(licenseExpireAt) : ''})` : ''}
          isRoundImage
        />
        <div className="tags-container">
          {!!vehicle && (
            <EntityLink
              to={vehicleDetailsId ? `/vehicles/${vehicleDetailsId}` : undefined}
              label={`Open vehicle ${vehicle.licensePlateNumber}`}
            >
              <Tags.Vehicle
                imageSrc={vehicleThumbnail}
                licensePlateNumber={vehicle.licensePlateNumber}
                model={`${vehicle.manufacturer ? `${vehicle.manufacturer} ` : ''}${vehicle.model || ''}`}
              />
            </EntityLink>
          )}
          {!vehicle && !lightVersion && !isFleetViewer && (
            <Button
              height="52px"
              width="200px"
              padding="24px 17px"
              background={theme.colors.outline}
              hoverBackground={theme.colors.inactive}
              onClick={(e) => handleAssignVehicle(e)}
            >
              Assign vehicle
            </Button>
          )}
        </div>
        {alerts !== undefined && (
          <Tags.LabeledIcon
            label={`${alerts} alert${alerts === 1 ? '' : 's'}`}
            icon={IconKeys.ALERT}
          />
        )}
        <Tags.LabeledIcon
          label={generateDistance(distance, !['monthly', 'weekly', 'daily', 'all'].includes(period))}
          icon={IconKeys.DISTANCE}
        />
        <Tags.ProceedToDetails />
      </ListItemWrapper>
      {modalState && (
        <Modal
          title="Assign vehicle"
          state={modalState}
          toggleState={toggleModalState}
        >
          <AssignVehicleToDriver
            onCancel={() => toggleModalState(false)}
            fleetId={fleetId}
            driverId={id}
            name={`${firstName} ${lastName}`}
            licenseName={licenseName || ''}
            avatar={avatarUrl || driverThumbnail}
          />
        </Modal>
      )}
    </>
  );
}

export default DriversItem;
